import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PrNumberPipe } from '../pipes/ea-number.pipe';

@Directive({
    selector: '[prNumericInput]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PrNumericInputDirective),
            multi: true,
        },
    ],
    standalone: false
})
export class PrNumericInputDirective implements ControlValueAccessor {
  @Input() allowDecimal: boolean = false;
  @Input() fractionSize: number = 2;

  private el: HTMLInputElement;
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private elementRef: ElementRef,
    private numberPipe: PrNumberPipe,
  ) {
    this.el = this.elementRef.nativeElement;
  }

  writeValue(value: any): void {
    if (value != null) {
      const formatted = this.numberPipe.transform(
        value,
        this.fractionSize,
        false,
      );
      this.el.value = formatted;
    } else {
      this.el.value = '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    // Empêcher le comportement par défaut d'Angular Forms
    // qui lirait la valeur avec espaces
    event.stopPropagation();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): boolean {
    const key = event.key;

    // Autoriser les touches de navigation et contrôle
    if (
      [
        'Backspace',
        'Tab',
        'End',
        'Home',
        'ArrowLeft',
        'ArrowRight',
        'Delete',
        'Enter',
      ].includes(key) ||
      (event.ctrlKey && ['a', 'c', 'v', 'x', 'z'].includes(key.toLowerCase()))
    ) {
      return true;
    }

    // Bloquer e, E, +, -
    if (['e', 'E', '+', '-'].includes(key)) {
      event.preventDefault();
      return false;
    }

    // Autoriser . ou , pour les décimales si allowDecimal est true
    if ((key === '.' || key === ',') && this.allowDecimal) {
      const currentValue = this.el.value.replace(/\s/g, '');
      // Bloquer si un point ou virgule existe déjà
      if (currentValue.includes('.') || currentValue.includes(',')) {
        event.preventDefault();
        return false;
      }
      return true;
    } else if (key === '.' || key === ',') {
      // Bloquer les décimales si allowDecimal est false
      event.preventDefault();
      return false;
    }

    // Autoriser uniquement les chiffres
    if (!/^\d$/.test(key)) {
      event.preventDefault();
      return false;
    }

    // Vérifier la limite de décimales (fractionSize)
    if (this.allowDecimal && /^\d$/.test(key)) {
      const currentValue = this.el.value.replace(/\s/g, '');
      const cursorPosition = this.el.selectionStart || 0;
      const selectionEnd = this.el.selectionEnd || 0;

      // Si on a une sélection, on autorise (car ça va remplacer)
      if (cursorPosition !== selectionEnd) {
        return true;
      }

      const parts = currentValue.split('.');
      if (parts.length === 2) {
        // Il y a déjà un point décimal
        const cursorPositionWithoutSpaces = currentValue
          .substring(0, cursorPosition)
          .replace(/\s/g, '').length;
        const decimalStartPosition = parts[0].length;

        // Si le curseur est après le point et qu'on a déjà fractionSize chiffres
        if (
          cursorPositionWithoutSpaces > decimalStartPosition &&
          parts[1].length >= this.fractionSize
        ) {
          event.preventDefault();
          return false;
        }
      }
    }

    return true;
  }

  @HostListener('keyup', ['$event'])
  onKeyUp($event: KeyboardEvent) {
    // Ignorer les touches de navigation (flèches gauche et droite)
    if ($event.key === 'ArrowLeft' || $event.key === 'ArrowRight') {
      return;
    }

    let value = this.el.value;
    const cursorPosition = this.el.selectionStart || 0;

    // Compter les espaces avant le curseur
    const spacesBefore =
      value.substring(0, cursorPosition).split(' ').length - 1;

    // Nettoyer la valeur avec sanitizeInput pour garantir qu'il n'y a qu'un seul point
    let result = this.sanitizeInput(value);

    // Retirer le 0 initial si suivi d'autres chiffres (sauf pour les décimales)
    if (result.length > 1 && result[0] === '0' && result[1] !== '.') {
      result = result.slice(1);
    }

    // Formatter avec les espaces de milliers
    const formatted = this.numberPipe.transform(
      result,
      this.fractionSize,
      false,
    );
    this.el.value = formatted;

    // Envoyer la valeur nettoyée au FormControl
    this.onChange(result);

    // Calculer la nouvelle position du curseur
    const charsBeforeCursor = cursorPosition - spacesBefore;
    const newValue = formatted;

    // Compter les espaces jusqu'à la position des caractères
    let charCount = 0;
    let newCursorPosition = 0;

    for (let i = 0; i < newValue.length; i++) {
      if (newValue[i] !== ' ') {
        charCount++;
      }
      if (charCount >= charsBeforeCursor) {
        newCursorPosition = i + 1;
        break;
      }
    }

    // Restaurer la position du curseur
    this.el.setSelectionRange(newCursorPosition, newCursorPosition);
  }

  @HostListener('blur')
  onBlur() {
    this.onTouched();

    let value = this.el.value.replace(/\s/g, '');

    if (!value || value === '') {
      this.onChange('');
      return;
    }

    // Nettoyer la valeur avant de reformater (enlever les points/virgules multiples)
    value = this.sanitizeInput(value);

    if (value) {
      // Ne jamais utiliser handleFraction: true pour éviter le double point et le padding
      const formatted = this.numberPipe.transform(
        value,
        this.fractionSize,
        false,
      );
      this.el.value = formatted;

      // Envoyer la valeur nettoyée au FormControl
      this.onChange(value);
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';

    let sanitized = this.sanitizeInput(pastedText);

    // Insérer le texte nettoyé
    const formatted = this.numberPipe.transform(
      sanitized,
      this.fractionSize,
      false,
    );
    this.el.value = formatted;

    // Envoyer la valeur nettoyée au FormControl
    this.onChange(sanitized);
  }

  private sanitizeInput(value: string): string {
    // Retirer les espaces
    let sanitized = value.replace(/\s/g, '');

    if (this.allowDecimal) {
      // Garder seulement les chiffres, points et virgules
      sanitized = sanitized.replace(/[^\d.,]/g, '');

      // Séparer sur le premier point ou virgule
      let hasDecimal = false;
      let result = '';

      for (let char of sanitized) {
        if ((char === '.' || char === ',') && !hasDecimal) {
          result += '.';
          hasDecimal = true;
        } else if (char !== '.' && char !== ',') {
          result += char;
        }
      }

      return result;
    } else {
      return sanitized.replace(/[^\d]/g, '');
    }
  }
}
